import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from "primeng/skeleton";
import { ToggleSwitch } from 'primeng/toggleswitch';
import { catchError, EMPTY, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { ApiClientService } from '../../../services/apiclient/api-client.service';
import { NotFound } from '../../../services/apiclient/not-found';
import { RoutingService } from '../../routing.service';
import { RequiredMarkerComponent } from './components/required-marker/required-marker.component';

@Component({
  selector: 'app-edit-rabbit',
  imports: [
    ButtonModule,
    ChipModule,
    DatePickerModule,
    FloatLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    MultiSelectModule,
    ReactiveFormsModule,
    RequiredMarkerComponent,
    SelectModule,
    SkeletonModule,
    ToggleSwitch,
  ],
  templateUrl: './edit-rabbit.component.html',
  styleUrl: './edit-rabbit.component.css',
})
export class EditRabbitComponent implements OnInit {
  private readonly apiClient = inject(ApiClientService);
  private readonly routingService = inject(RoutingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);

  protected readonly id = input<string | undefined>(undefined);

  protected readonly formFieldNames = {
    name: 'name',
    isDisabled: 'isDisabled',
  } as const;

  protected readonly formGroup = new FormGroup({
    [this.formFieldNames.name]: new FormControl<string | null>({ value: null, disabled: true }, [Validators.required]),
    [this.formFieldNames.isDisabled]: new FormControl<boolean>(false),
  });

  protected readonly isRabbitDataLoaded = signal(false);

  ngOnInit(): void {
    this.loadEntryDetailsAndEnableControls();
  }

  private loadEntryDetailsAndEnableControls() {
    const rabbitId = this.id();

    if (rabbitId === undefined || rabbitId === null) {
      this.enableFormControls();
      this.isRabbitDataLoaded.set(true);
      return;
    }

    this.apiClient
      .getRabbitById(rabbitId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => this.handleGetError(error)),
        switchMap((result) => {
          if (result instanceof NotFound) {
            this.messageService.add({
              id: 'rabbit-not-found',
              severity: 'error',
              summary: 'Kaninchen nicht gefunden',
              detail: 'Das Kaninchen konnte nicht gefunden werden.',
            });
            this.navigateToOverviewPage();
            return EMPTY;
          }

          return of(result);
        }),
        tap((rabbit) => {
          this.formGroup.patchValue({
            [this.formFieldNames.name]: rabbit.name,
            [this.formFieldNames.isDisabled]: rabbit.isDisabled,
          });
        }),
        tap(() => {
          this.enableFormControls();
          this.isRabbitDataLoaded.set(true);
        }),
      )
      .subscribe();

  }

  private enableFormControls(): void {
    for (const controlName of Object.values(this.formFieldNames)) {
      const control = this.formGroup.get(controlName);
      if (control) {
        control.enable();
      } else {
        console.warn(`Form control '${controlName}' not found.`);
      }
    }
  }

  protected onSubmit(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    var rabbitId = this.id();
    if (rabbitId === undefined || rabbitId === null) {
      this.createRabbit();
      return;
    }

    this.updateRabbit(rabbitId);
  }

  private getFormData() {
    return {
      name: this.formGroup.controls[this.formFieldNames.name].value!,
      isDisabled: this.formGroup.controls[this.formFieldNames.isDisabled].value!,
    };
  }

  createRabbit() {
    const data = this.getFormData();
    this.apiClient.createRabbit(data.name, data.isDisabled)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => this.handleCreateOrUpdateError(error, false)),
        switchMap(() => this.routingService.navigateToRabbits())
      )
      .subscribe();
  }

  updateRabbit(id: string) {
    const data = this.getFormData();
    this.apiClient.updateRabbit(id, data.name, data.isDisabled)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => this.handleCreateOrUpdateError(error, true)),
        switchMap(() => this.routingService.navigateToRabbits())
      )
      .subscribe();
  }

  protected async navigateToOverviewPage(): Promise<void> {
    await this.routingService.navigateToRabbits();
  }


  private handleGetError(error: unknown): Observable<never> {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }

    switch (true) {
      case error.status >= 500 && error.status <= 599:
        this.messageService.add({
          severity: 'error',
          summary: 'Serverfehler',
          detail:
            'Beim Abrufen des Kaninchens ist ein Fehler aufgetreten. Bitte versuche es erneut.',
        });
        break;
      default:
        console.error(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Unerwarteter Fehler',
          detail:
            'Beim Abrufen des Kaninchens hat der Server eine unerwartete Antwort zurückgegeben.',
        });
        break;
    }

    return EMPTY;
  }

  private handleCreateOrUpdateError(error: unknown, isUpdate: boolean): Observable<never> {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }

    const action = isUpdate ? 'Aktualisieren' : 'Erstellen';

    switch (true) {
      case error.status >= 500 && error.status <= 599:
        this.messageService.add({
          severity: 'error',
          summary: 'Serverfehler',
          detail:
            `Beim ${action} des Eintrags ist ein Fehler aufgetreten. Bitte versuche es erneut.`,
        });
        break;
      case error.status === 400:
        console.error(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Clientfehler',
          detail:
            'Die Anwendung scheint falsche Daten an den Server zu senden.',
        });
        break;
      case error.status === 409:
        this.messageService.add({
          severity: 'warn',
          summary: 'Konflikt',
          detail:
            'Es existiert bereits ein Kaninchen mit diesem Namen. Bitte wähle einen anderen Namen.',
        });
        break;
      default:
        console.error(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Unerwarteter Fehler',
          detail:
            `Beim ${action} des Eintrags hat der Server eine unerwartete Antwort zurückgegeben.`,
        });
        break;
    }

    return EMPTY;
  }
}
